import { FC, useLayoutEffect, useMemo, useCallback, useState, ChangeEvent } from 'react';
import { Dropdown, Loading, Section, Input, Button, Table, ReportCard } from '../../../../components';
import { formataPlaca, moneyFormatter, TagStatusLabel } from '../../../../utils';
import { useToastStore } from '../../../../stores';
import {
	TAnuncio,
	TConcessionaria,
	TGrupoConcessionaria,
	getAllAdminDealers,
	getAllGroupDealers,
	getAllAdminOffers
} from '../../../../services';
import { Option } from '../../../../components/dropdown/Dropdown';
import { Line } from 'react-chartjs-2';
import {
	Form,
	FullLine,
	HalfLine,
	HalfLineButton,
	NoData,
	Separator,
	TableContainer,
	TableWrapper,
	TitleContainer,
} from '../../Reports.styles';

interface IGraphLineData {
	label: string;
	qtde: number;
}

const AdminSellerReport: FC = () => {
	const { setToast } = useToastStore();
	const [loading, setLoading] = useState(false);
	const [filteredOffers, setFilteredOffers] = useState<TAnuncio[]>([]);
	const [placa, setPlaca] = useState('');
	const [startDate, setStartDate] = useState('');
	const [endDate, setEndDate] = useState('');
	const [startSellDate, setStartSellDate] = useState('');
	const [endSellDate, setEndSellDate] = useState('');
	const [graphLineData, setGraphLineData] = useState<IGraphLineData[]>([]);
	const [gruposConcessionarias, setGruposConcessionarias] = useState<TGrupoConcessionaria[]>([]);
	const [concessionarias, setConcessionarias] = useState<TConcessionaria[]>([]);
	const [filteredConcessionarias, setFilteredConcessionarias] = useState<TConcessionaria[]>([]);
	const [clickGenerate, setClickGenerate] = useState(false);

	const statusOptions = [
		{ label: 'Todos os status', value: 99},
		{ label: TagStatusLabel.Open, value: 0},
		{ label: TagStatusLabel.Sold, value: 1},
		{ label: TagStatusLabel.Closed, value: 2},
	];

	const [selectedStatusOption, setSelectedStatusOption] = useState<Option>(
		statusOptions[0]
	);
	
	const grupoOptions = useMemo(() => {
		const options =
			gruposConcessionarias
				.map((item) => ({
					value: item.id.toString(),
					label: item.grupoConcessionaria || '',
				}))
				.sort((a, b) => a.label.localeCompare(b.label)) || [];
		options.unshift({ value: '0', label: 'Todos os Grupos' });
		return options;
	}, [gruposConcessionarias]);

	const [selectedGroupOption, setSelectedGroupOption] = useState<Option>(
		grupoOptions[0] || { value: '0', label: 'Nenhum grupo de concessionária relacionado' }
	);

	const dealerOptions = useMemo(() => {
		const options =
			filteredConcessionarias
				.map((item) => ({
					value: item.id.toString(),
					label: item.fantasia || '',
				}))
				.sort((a, b) => a.label.localeCompare(b.label)) || [];
		options.unshift({ value: '0', label: 'Todas as Concessionárias' });
		return options;
	}, [filteredConcessionarias]);

	const [selectedOption, setSelectedOption] = useState<Option>(
		dealerOptions[0] || { value: '0', label: 'Nenhuma concessionária relacionada' }
	);

	const handleSelectOption = (option: Option) => {
		setSelectedOption(option);
	};

	const handleSelectGroupOption = (option: Option) => {
		setSelectedGroupOption(option);
	};

	const handleSelectStatusOption = (option: Option) => {
		setSelectedStatusOption(option);

		if (option.value === '0') {
			setFilteredConcessionarias(concessionarias);
		} else {
			const filtered = concessionarias.filter(
				(concessionaria) => concessionaria.grupoConcessionaria.id.toString() === option.value
			);
			setFilteredConcessionarias(filtered);
		}
	};

	const handleSetPlaca = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
		const { value } = e.target;
		const newPlaca = formataPlaca(value);
		setPlaca(newPlaca)
	};

	useLayoutEffect(() => {
		const fetchDealerGroups = async () => {
			setLoading(true);
			try {
				const response = await getAllGroupDealers();
				setGruposConcessionarias(response || []);
			} catch {
				setToast({ message: 'Erro ao buscar grupos de concessionária', variant: 'error' });
			} finally {
				setLoading(false);
			}
		};

		const fetchAllDealers = async () => {
			setLoading(true);
			try {
				const response = await getAllAdminDealers();
				setConcessionarias(response || []);
				setFilteredConcessionarias(response || []);
			} catch {
				setToast({ message: 'Erro ao buscar as concessionárias!', variant: 'error' });
			} finally {
				setLoading(false);
			}
		};

		fetchDealerGroups();
		fetchAllDealers();
	}, [setToast]);

	const fetchData = async () => {
		if (startDate && endDate && endDate >= startDate) {
			setLoading(true);
			const tagStatus = selectedStatusOption.value !== 99 ? selectedStatusOption.value.toString() : undefined;

			const body = {
				dataCadastroInicial: `${startDate}T00:00:00.000Z`,
				dataCadastroFinal: `${endDate}T23:59:59.999Z`,
				tagAtivo: '1',
				tagStatus: tagStatus, 
			};
			const data = await getAllAdminOffers(body);
			if (!data) {
				setToast({ variant: 'error', message: 'Erro ao buscar dados!' });
				setLoading(false);
				return;
			}

			const selectedIds = selectedOption.value.toString();
			const selectedGroup = selectedGroupOption.value.toString();

			const filtered = data.filter((offer) => {
				const isWithinSellDateRange =
					(!startSellDate || new Date(offer.dataVenda!) >= new Date(`${startSellDate}T00:00:00.000Z`)) &&
					(!endSellDate || new Date(offer.dataVenda!) <= new Date(`${endSellDate}T23:59:59.999Z`));

				const matchesPlate = !placa || offer.placa.includes(placa);

				const matchesDealerOrGroup =
					(selectedIds === "0" && selectedGroup === "0") ||
					(selectedIds !== "0" && selectedIds === offer.concessionaria.id.toString()) ||
					(selectedGroup !== "0" && selectedIds === "0" && selectedGroup === offer.concessionaria.grupoConcessionaria.id.toString());

				return isWithinSellDateRange && matchesPlate && matchesDealerOrGroup;
			});

			const salesCountByMonthYear: IGraphLineData[] = [];
			filtered.forEach((offer) => {
				const dateCadastro = new Date(offer.dataCadastro);
				const label = `${dateCadastro.getMonth() + 1}/${dateCadastro.getFullYear()}`;
				const existingEntry = salesCountByMonthYear.find((entry) => entry.label === label);

				if (existingEntry) {
					existingEntry.qtde++;
				} else {
					salesCountByMonthYear.push({ label, qtde: 1 });
				}
			});

			setGraphLineData(salesCountByMonthYear);
			setFilteredOffers(filtered);
			setClickGenerate(true);
			setLoading(false);
		}
	};

	const tableColumns = useMemo(
		() => [
			{ id: 'registerDate', label: 'Data de cadastro', width: '100px' },
			{ id: 'dealer', label: 'Anunciante' },
			{ id: 'buyer', label: 'Comprador' },
			{ id: 'plate', label: 'Placa' },
			{ id: 'vehicle', label: 'Veículo' },
			{ id: 'sellDate', label: 'Data de Venda', width: '100px' },
			{ id: 'value', label: 'Valor' },
			{ id: 'views', label: 'Views' },
		],
		[]
	);

	const rowMapper = useCallback(
		(offer: TAnuncio) => ({
			vehicle: {
				value: `${offer.veiculoModelo.veiculoMarca.veiculoMarca} ${offer.veiculoModelo.veiculoModelo} ${offer.anoFabricacao}/${offer.anoModelo}`,
				rawValue: `${offer.veiculoModelo.veiculoMarca.veiculoMarca} ${offer.veiculoModelo.veiculoModelo} ${offer.anoFabricacao}/${offer.anoModelo}`,
			},
			plate: {
				value: offer.placa,
				rawValue: offer.placa,
			},
			dealer: {
				value: offer.concessionaria.fantasia,
				rawValue: offer.concessionaria.fantasia,
			},
			buyer: {
				value: offer.revendaCompradora?.fantasia,
				rawValue: offer.revendaCompradora?.fantasia,
			},
			registerDate: {
				value: new Date(offer.dataCadastro).toLocaleDateString('pt-BR'),
				rawValue: new Date(offer.dataCadastro).getTime(),
			},
			sellDate: {
				value: new Date(offer.dataVenda).toLocaleDateString('pt-BR'),
				rawValue: new Date(offer.dataVenda).getTime(),
			},
			value: {
				value: moneyFormatter(offer.valorCompreAgora),
				rawValue: offer.valorCompreAgora,
			},
			views: {
				value: offer.qtdTotalClick || 0,
				rawValue: offer.qtdTotalClick || 0,
			},
			
		}),
		[]
	);

	const MemoizedTable = useMemo(
		() => <Table columns={tableColumns} rows={filteredOffers?.map(rowMapper)} paginate exportToPDF exportToCSV />,
		[filteredOffers, rowMapper, tableColumns]
	);

	return (
		<Section>
			<TitleContainer>Relatório de Anúncios</TitleContainer>
			<Separator />
			<Form>
				<HalfLine>
					<Dropdown
						options={grupoOptions}
						selected={selectedGroupOption}
						onSelect={handleSelectGroupOption}
					/>
				</HalfLine>
				<HalfLine>
					<Dropdown
						options={dealerOptions}
						selected={selectedOption}
						onSelect={handleSelectOption}
						searchable
					/>
				</HalfLine>
				<HalfLine>
					<Dropdown
						options={statusOptions}
						selected={selectedStatusOption}
						onSelect={handleSelectStatusOption}
					/>
				</HalfLine>
				<HalfLine>
					<Input
						label="Data de Cadastro Inicial*"
						type="date"
						variant='text'
						value={startDate}
						onChange={(e) => setStartDate(e.target.value)}
					/>
				</HalfLine>
				<HalfLine>
					<Input
						label="Data de Cadastro Final*"
						type="date"
						variant='text'
						value={endDate}
						onChange={(e) => setEndDate(e.target.value)}
						min={startDate}
					/>
				</HalfLine>
				<HalfLine>
					<Input
						label="Placa"
						placeholder="Placa"
						type="text"
						variant='text'
						value={placa}
						onChange={handleSetPlaca}
						maxLength={8}
					/>
				</HalfLine>
				<HalfLine>
					<Input
						label="Data de Venda Inicial"
						type="date"
						variant='text'
						value={startSellDate}
						onChange={(e) => setStartSellDate(e.target.value)}
					/>
				</HalfLine>
				<HalfLine>
					<Input
						label="Data de Venda Final"
						type="date"
						variant='text'
						value={endSellDate}
						onChange={(e) => setEndSellDate(e.target.value)}
						min={startSellDate}
					/>
				</HalfLine>
				<HalfLineButton>
					<FullLine>
						<HalfLineButton>
							<Button
								variant='large'
								fitContent
								onClick={fetchData}
								disabled={(!startDate || !endDate) && !placa}
							>
								Gerar
							</Button>
						</HalfLineButton>
					</FullLine>
				</HalfLineButton>
			</Form>
			{loading && <Loading />}
			{filteredOffers.length > 0 && (
				<ReportCard
					data={[
						{
							color: '#FF9800',
							title: 'Qtde Total de Anúncios no Período',
							value: filteredOffers.length,
						},
						{
							color: '#4CAF50',
							title: 'Qtde Anúncios Vendidos no Período',
							value: filteredOffers.filter((offer) => offer.tagStatus === "1").length,
						},
						{
							color: '#2196F3',
							title: 'Qtde Anúncios Em Aberto no Período',
							value: filteredOffers.filter((offer) => offer.tagStatus === "0").length,
						},
						{
							color: '#f44336',
							title: 'Qtde Anúncios Expirados no Período',
							value: filteredOffers.filter((offer) => offer.tagStatus === "2").length,
						},
					]}
				/>

			)}
			<TableContainer>
				{(loading || (filteredOffers?.length || 0) > 0) && (
					<TableWrapper>{loading ?  <Loading /> : MemoizedTable}</TableWrapper>
				)}
				{!loading && clickGenerate && filteredOffers?.length === 0 && (
					<NoData>Nenhum registro encontrado</NoData>
				)}
				{graphLineData.length > 0 && (
					<FullLine>
						<Line
							data={{
								labels: graphLineData.map((item) => item.label),
								datasets: [
									{
										label: 'Anúncios Cadastrados por período',
										data: graphLineData.map((item) => item.qtde),
										backgroundColor: 'rgba(255, 102, 0, 0.5)',
										borderColor: 'rgba(255, 102, 0)',
									},
								],
							}}
						/>
					</FullLine>
				)}
			</TableContainer>
			
		</Section>
	);
};

export default AdminSellerReport;
